// ============================================================================
// components/mail/MailMessageList.tsx — список писем выбранной папки.
// Только представление: фильтрация и загрузка — в экране и lib/mail/mailData.
// ============================================================================

import type { MailMessage } from '@/lib/mail/mailData';
import { messageSnippet, senderLabel } from '@/lib/mail/mailData';
import { formatValue } from '@/lib/format';
import { cn } from '@/lib/utils';

interface MailMessageListProps {
  messages: MailMessage[];
  selectedId: string;
  onSelect: (message: MailMessage) => void;
  loading: boolean;
  emptyText: string;
}

function messageDate(message: MailMessage): string {
  const value = message.receivedAt || message.createdAt;
  return formatValue(value, '', 'timestamp');
}

function messageSubject(message: MailMessage): string {
  return message.subject || '(без темы)';
}

export function MailMessageList({
  messages,
  selectedId,
  onSelect,
  loading,
  emptyText,
}: MailMessageListProps) {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      {loading && messages.length === 0 && (
        <div className="p-4 text-sm text-muted-foreground">Загрузка писем…</div>
      )}
      {!loading && messages.length === 0 && (
        <div className="p-4 text-sm text-muted-foreground">{emptyText}</div>
      )}
      {messages.map((message) => {
        const active = message.id === selectedId;
        const snippet = messageSnippet(message);
        return (
          <button
            key={message.id}
            type="button"
            onClick={() => onSelect(message)}
            className={cn(
              'flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left transition-colors',
              active ? 'bg-primary/10' : 'hover:bg-accent/60',
            )}
          >
            <div className="flex w-full items-center gap-2">
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  message.isRead ? 'bg-transparent' : 'bg-primary',
                )}
                title={message.isRead ? 'Прочитано' : 'Не прочитано'}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-sm',
                  message.isRead ? 'text-foreground' : 'font-semibold text-foreground',
                )}
              >
                {senderLabel(message)}
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {messageDate(message)}
              </span>
            </div>
            <div className="flex w-full items-center gap-2 pl-4">
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-sm',
                  message.isRead ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {messageSubject(message)}
              </span>
            </div>
            {snippet && (
              <div className="line-clamp-2 pl-4 text-xs text-muted-foreground">{snippet}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}
